import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit3, X, AlertCircle, Percent } from 'lucide-react';
import { API } from '../config';
import { devLog } from '../utils/devLog';

const todayIso = () => new Date().toISOString().slice(0, 10);

// Returns YYYY-MM-DD that is `days` after `startIso`
const addDays = (startIso, days) => {
  const d = new Date(startIso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const EmptyForm = (start = todayIso()) => ({
  period_start: start,
  period_end: addDays(start, 14),
  loss_pct: 0,
  notes: '',
});

const Co2LossView = ({ plants, selectedPlant, setSelectedPlant, token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EmptyForm());
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!selectedPlant) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/plants/${selectedPlant.id}/co2-losses`, { headers: authHeaders });
      setEntries(res.data || []);
    } catch (err) {
      devLog.error('Failed to load CO2 losses:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlant, token]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EmptyForm());
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      period_start: entry.period_start,
      period_end: entry.period_end,
      loss_pct: entry.loss_pct,
      notes: entry.notes || '',
    });
    setShowForm(true);
  };

  const handleStartChange = (val) => {
    // Auto-bump end to start+14d when adding a brand new entry (UX nicety)
    setForm(prev => ({
      ...prev,
      period_start: val,
      period_end: editingId ? prev.period_end : addDays(val, 14),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.period_start > form.period_end) {
      alert('period_start must be on or before period_end');
      return;
    }
    if (form.loss_pct < 0 || form.loss_pct >= 100) {
      alert('Loss % must be between 0 and 100 (exclusive)');
      return;
    }
    try {
      setLoading(true);
      if (editingId) {
        await axios.put(`${API}/plants/${selectedPlant.id}/co2-losses/${editingId}`, form, { headers: authHeaders });
      } else {
        await axios.post(`${API}/plants/${selectedPlant.id}/co2-losses`, form, { headers: authHeaders });
      }
      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Failed to save'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this CO₂ loss entry?')) return;
    try {
      setLoading(true);
      await axios.delete(`${API}/plants/${selectedPlant.id}/co2-losses/${id}`, { headers: authHeaders });
      await load();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Failed to delete'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Select Plant</label>
        <select
          value={selectedPlant?.id || ''}
          onChange={(e) => setSelectedPlant(plants.find(p => p.id === e.target.value))}
          className="w-full px-4 py-3 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA] text-base font-medium"
          data-testid="co2-loss-plant-select"
        >
          {plants.map(p => (
            <option key={p.id} value={p.id}>{p.name}{p.location ? ` (${p.location})` : ''}</option>
          ))}
        </select>
        <p className="mt-3 text-xs sm:text-sm text-[#156ACA] bg-blue-50 border border-blue-200 rounded-md px-3 py-2" data-testid="co2-loss-info">
          Enter the CO₂ loss % observed for each ~15-day window. The dashboard&apos;s CO₂ purchase cost will be grossed
          up by this % for all shifts whose date falls in the window.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-start justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <Percent className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-[#156ACA] flex-shrink-0" /> CO₂ Loss Entries
            </h2>
            <p className="text-sm text-gray-600 mt-1 break-words">Fortnightly loss % for <strong>{selectedPlant?.name}</strong></p>
          </div>
          <button
            onClick={openAdd}
            className="bg-[#156ACA] hover:bg-[#0F5299] text-white font-semibold py-2.5 px-4 min-h-[44px] rounded-lg flex items-center whitespace-nowrap"
            data-testid="add-co2-loss-btn"
          >
            <Plus className="h-5 w-5 mr-2" /> Add Entry
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="co2-loss-form">
            <div>
              <label className="text-xs font-semibold text-gray-700">Period Start</label>
              <input
                type="date"
                value={form.period_start}
                onChange={(e) => handleStartChange(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="co2-loss-period-start"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Period End</label>
              <input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm(prev => ({ ...prev, period_end: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="co2-loss-period-end"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Loss % (0–99.9)</label>
              <input
                type="number" step="0.01" min="0" max="99.9"
                value={form.loss_pct}
                onChange={(e) => setForm(prev => ({ ...prev, loss_pct: parseFloat(e.target.value) || 0 }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg font-semibold"
                data-testid="co2-loss-pct"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Notes (optional)</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g. monsoon spike"
                data-testid="co2-loss-notes"
              />
            </div>
            <div className="sm:col-span-2 md:col-span-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
              <button
                type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                className="px-4 py-2.5 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center"
              >
                <X className="h-4 w-4 mr-1" /> Cancel
              </button>
              <button
                type="submit" disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 min-h-[44px] rounded-lg disabled:opacity-50"
                data-testid="save-co2-loss-btn"
              >
                {editingId ? 'Save Changes' : 'Add Entry'}
              </button>
            </div>
          </form>
        )}

        {loading && entries.length === 0 ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#156ACA]"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-testid="co2-loss-empty">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No CO₂ loss entries yet</p>
            <p className="text-sm text-gray-500 mt-1">Add an entry to start gross-up of CO₂ purchase costs</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-2">Period</th>
                  <th className="text-right px-4 py-2">Loss %</th>
                  <th className="text-left px-4 py-2">Notes</th>
                  <th className="text-left px-4 py-2">Created By</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`co2-loss-row-${e.id}`}>
                    <td className="px-4 py-2 font-medium text-gray-900">{e.period_start} → {e.period_end}</td>
                    <td className="px-4 py-2 text-right font-bold text-red-600">{Number(e.loss_pct).toFixed(2)}%</td>
                    <td className="px-4 py-2 text-gray-700">{e.notes || '—'}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{e.created_by || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => openEdit(e)} className="text-[#156ACA] hover:text-[#0F5299] p-2 mr-1" data-testid={`edit-co2-loss-${e.id}`}>
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(e.id)} className="text-red-600 hover:text-red-800 p-2" data-testid={`delete-co2-loss-${e.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Co2LossView;
